import React, { useContext, useEffect, useMemo, useState, useRef, useCallback } from "react";
import _ from "lodash";
import styles from "./KanbanBoard.module.scss";
import { SnackbarContext } from "../../context/snackbarContext";
import { closestCorners, DndContext, DragOverlay, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { arrayMove, horizontalListSortingStrategy, SortableContext } from "@dnd-kit/sortable";
import useDidUpdateEffect from "../../hooks/useDidUpdateEffect";

import { Button, useTheme } from "@material-ui/core";

import KanbanColumn from "./KanbanColumn";
import { createPortal } from "react-dom";
import KanbanCard from "./KanbanCard";
import KanbanColumnVirtualized from "./KanbanColumnVirtualized";
import { useMutation } from "@apollo/client";
import { FetchContext } from "../../context/fetchContext";

const normalizeKanbanItem = ({ item, type, itemFieldKeyDict, itemFieldValDict }) => {
  const positionKey = _.get(itemFieldKeyDict, [type, "kanbanPosition"], "kanbanPosition");
  const statusKey = _.get(itemFieldKeyDict, [type, "kanbanStatus"], "kanbanStatus");

  let positionVal = _.get(item, positionKey);
  positionVal = _.get(itemFieldValDict, [type, "kanbanPosition", positionVal], positionVal);

  let statusVal = _.get(item, statusKey);
  statusVal = _.get(itemFieldValDict, [type, "kanbanStatus", statusVal], statusVal);

  const normalizedItem = _.omit(item, [positionKey, statusKey]);
  return { ...normalizedItem, kanbanPosition: positionVal, kanbanStatus: statusVal, type };
};

const getNextAvailKanbanPos = (maxPos) => {
  return Math.ceil(maxPos / 1000) * 1000 + 1000;
};

const KanbanBoard = ({
  columns = [],
  filters,
  itemFieldKeyDict,
  itemFieldValDict,
  columnDataQuery,
  columnDataQueryVarDict,
  updateItemMutation,
  updateItemMutationVarDict,
  queryName,
  mutationName,
  localStorageKey = "kanbanColumns",
  sort = "desc",
  getItemType = () => {},
  renderCardContents,
}) => {
  const theme = useTheme();
  const { snack } = useContext(SnackbarContext);
  const { fetch } = useContext(FetchContext);

  const [itemsDict, setItemsDict] = useState({});
  const [canFetch, setCanFetch] = useState(true);
  const [refreshCounter, setRefreshCounter] = useState(0);
  const refreshInProgressRef = useRef(false);

  const [columnTotals, setColumnTotals] = useState({});
  const [columnArr, setColumnArr] = useState(() => {
    const saved = localStorage.getItem(localStorageKey);
    return saved ? JSON.parse(saved) : columns.map((col) => ({ ...col, expanded: true }));
  });

  const [activeColumn, setActiveColumn] = useState(null); // the column being dragged
  const [activeCard, setActiveCard] = useState(null); // the card being dragged
  // const [overCardHistory, setOverCardHistory] = useState([]); // the list of cards hovered during a drag and drop instance

  const activeCardColRef = useRef(null);

  const [moveKanbanCard] = useMutation(updateItemMutation);

  const columnIds = useMemo(() => {
    return columnArr.map((col) => col.id);
  }, [columnArr]);

  // const sensors = useSensors(
  //   useSensor(PointerSensor, {
  //     activationConstraint: {
  //       distance: 5, // need to drag for 5px for the dnd to trigger
  //     },
  //   })
  // );

  const triggerRefresh = useCallback(() => {
    if (refreshInProgressRef.current) return; // Prevent multiple concurrent refreshes

    refreshInProgressRef.current = true;

    setCanFetch(false);
    setItemsDict({});
  }, []);

  const clearTrackingStates = () => {
    setActiveColumn(null);
    // setOverCardHistory([]);
    setActiveCard(null);
  };

  const handleExpandColumn = (colId) => {
    setColumnArr((cols) => cols.map((col) => (_.get(col, "id") === colId ? { ...col, expanded: !col.expanded } : col)));
  };

  const handleNewlyLoadedItems = (status) => (newItems) => {
    const normalizedItems = newItems.map((item) =>
      normalizeKanbanItem({ item, type: getItemType(item), itemFieldKeyDict, itemFieldValDict })
    );

    setItemsDict((prev) => {
      const dictCopy = _.cloneDeep(prev);

      /* 
      // Uncomment the following if newItems contains multiple status items
      // Group normalized items by status for easy merging
      const groupedByStatus = _.groupBy(normalizedItems, "status");

      // Iterate over groups and merge it into the overall items dictionary
      _.forEach(groupedByStatus, (items, status) => {
        // do below inside here
      });
      */

      // Create a dictionary of existing status items using id as key
      const statusColDict = _.keyBy(_.get(dictCopy, status, []), "id");

      // Merge or add new items
      normalizedItems.forEach((item) => {
        statusColDict[item.id] = item; // Replace or insert
      });

      // Update the overall items dicionary with merged values
      dictCopy[status] = _.orderBy(_.values(statusColDict), "kanbanPosition", sort);

      return dictCopy;
    });
  };

  const handleUpdateKanban = async (itemUpdate) => {
    const { type, kanbanStatus, kanbanPosition } = itemUpdate;

    try {
      const res = await moveKanbanCard({
        variables: {
          ...itemUpdate,
          [_.get(updateItemMutationVarDict, "kanbanPosition", "kanbanPosition")]: kanbanPosition,
          [_.get(updateItemMutationVarDict, "kanbanStatus", "kanbanStatus")]: kanbanStatus,
        },
      });
      const data = _.get(res, ["data", mutationName]);

      if (data) {
        setItemsDict((prev) => {
          return _.mapValues(prev, (items) =>
            _.map(items, (item) =>
              item.id === data.id ? normalizeKanbanItem({ item: data, type: getItemType(data), itemFieldKeyDict, itemFieldValDict }) : item
            )
          );
        });
        snack(`Updated ${_.lowerCase(type)}`);
      }
    } catch (err) {
      console.log({ err });
      triggerRefresh();
      snack(`Error updating ${_.lowerCase(type)}`, "error");
    }
  };

  const handleDragStart = (event) => {
    const { active } = event;
    const dragRef = active.data.current;

    if (dragRef.type === "Column") {
      setActiveColumn(dragRef.column);
    } else if (dragRef.type === "Card") {
      setActiveCard(dragRef.item);
      activeCardColRef.current = _.get(dragRef, "item.kanbanStatus");
    }
    return;
  };

  const handleDragOver = (event) => {
    const { active, over } = event;

    if (!over) return;

    const activeId = active.id;
    const overId = over.id;

    if (activeId === overId) {
      /* Handles edge case where when card is dragged to ends of a column the active and over are the same,
      // null is added to "over" history to detect this */
      // setOverCardHistory((prev) => [...prev, null]);
      return;
    }

    const activeRef = active.data.current;
    const overRef = over.data.current;

    const activeType = activeRef.type;
    const overType = overRef.type;

    /* **NOTE: Problem when using virtualized columns - the active ref data sometimes gets lost during cross column dragging
    // but the id remains */

    if (activeType === "Card") {
      const activeCol = activeCardColRef.current;

      let overCol = overId;
      if (overType === "Card") {
        // setOverCardHistory((prev) => [...prev, overRef.item]);
        overCol = _.get(overRef, "item.kanbanStatus");
      }

      if (activeCol !== overCol) {
        activeCardColRef.current = overCol;

        /* The setTimeout is potentially needed to prevent an error related to the maximum update depth being exceeded
        // when swapping cards to a different column */
        setTimeout(() => {
          setItemsDict((prev) => {
            const dictCopy = _.cloneDeep(prev);

            const itemToMove = _.remove(dictCopy[activeCol], (item) => item.id === activeId)[0];
            _.set(itemToMove, "kanbanStatus", overCol);

            if (_.has(dictCopy, overCol)) {
              dictCopy[overCol].push(itemToMove);
            } else {
              dictCopy[overCol] = [itemToMove];
            }

            return dictCopy;
          });
        }, 0);
      }
    }
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (!over) {
      clearTrackingStates();
      return;
    }

    const activeId = active.id;
    const overId = over.id;

    const activeRef = active.data.current;
    const overRef = over.data.current;

    const activeType = activeRef.type;
    const overType = overRef.type;

    if (activeType === "Column" && overType === "Column") {
      if (activeId === overId) {
        clearTrackingStates();
        return;
      }

      setColumnArr((cols) => {
        const activeIdx = _.findIndex(cols, ["id", activeId]);
        const overIdx = _.findIndex(cols, ["id", overId]);

        return arrayMove(cols, activeIdx, overIdx);
      });
    } else if (activeType === "Card") {
      const sourceColumn = _.get(activeCard, "kanbanStatus");

      const destinationColumn = overType === "Card" ? _.get(overRef, "item.kanbanStatus") : overId;
      const sameColReorder = sourceColumn === destinationColumn;
      const currPos = _.get(activeCard, "kanbanPosition");

      setItemsDict((prev) => {
        const dictCopy = _.cloneDeep(prev);
        const destColItems = dictCopy[destinationColumn];

        let newDestColItems = destColItems;
        if (activeId !== overId) {
          /* When active id is equal to over id, user is attempting to move it to the end of another column which 
          // is already done when items are hovered over a different column. Therefore only do reorder when this is not
          // the case */
          const destColActiveItemIdx = _.findIndex(destColItems, ["id", activeId]);
          const destColOverItemIdx = _.findIndex(destColItems, ["id", overId]);

          newDestColItems = arrayMove(destColItems, destColActiveItemIdx, destColOverItemIdx);
        }

        const movedItemIdx = _.findIndex(newDestColItems, ["id", activeId]);

        const prevNeighbor = movedItemIdx > 0 ? newDestColItems[movedItemIdx - 1] : null;
        const nextNeighbor = movedItemIdx < newDestColItems.length - 1 ? newDestColItems[movedItemIdx + 1] : null;

        const prevPos = _.get(prevNeighbor, "kanbanPosition", null);
        const nextPos = _.get(nextNeighbor, "kanbanPosition", null);

        let newPos = 1000; // default position for when items are moved to empty columns
        if (_.isNil(prevPos) && !_.isNil(nextPos)) {
          newPos = sort === "desc" ? getNextAvailKanbanPos(nextPos) : Math.round(nextPos / 2);
        } else if (_.isNil(nextPos) && !_.isNil(prevPos)) {
          newPos = sort === "desc" ? Math.round(prevPos / 2) : getNextAvailKanbanPos(prevPos);
        } else if (!_.isNil(prevPos) && !_.isNil(nextPos)) {
          newPos = Math.round((prevPos + nextPos) / 2);
        }

        if (currPos !== newPos || !sameColReorder) {
          _.set(newDestColItems, [movedItemIdx.toString(), "kanbanPosition"], newPos);

          const update = { id: activeId, kanbanPosition: newPos, type: _.get(activeRef, "item.type") };
          if (!sameColReorder) {
            _.set(update, "kanbanStatus", destinationColumn);

            setColumnTotals((prev) => {
              return {
                ...prev,
                [sourceColumn]: _.get(prev, sourceColumn, 0) - 1,
                [destinationColumn]: _.get(prev, destinationColumn, 0) + 1,
              };
            });
          }

          handleUpdateKanban(update);
        }

        return { ...dictCopy, [destinationColumn]: newDestColItems };
      });
    }

    clearTrackingStates();
  };

  function customCollisionDetectionAlgorithm({ droppableContainers, ...args }) {
    const { active } = args;
    const activeType = _.get(active, "data.current.type");

    const validDroppables = droppableContainers.filter((droppable) => {
      let extraCond = false;
      if (activeType === "Card") {
        const colItems = _.get(droppable, "data.current.column.items");
        extraCond = _.isArray(colItems) && _.isEmpty(colItems);
      }

      return _.get(droppable, "data.current.type") === activeType || extraCond;
    });

    return closestCorners({
      ...args,
      droppableContainers: validDroppables,
    });
  }

  useEffect(() => {
    if (refreshInProgressRef.current && _.isEmpty(itemsDict)) {
      // Data is now empty, allow fetching
      setCanFetch(true);
      setRefreshCounter((prev) => prev + 1);

      refreshInProgressRef.current = false;
    }
  }, [itemsDict]);

  useEffect(() => {
    triggerRefresh();
  }, [filters, fetch, triggerRefresh]);

  useEffect(() => {
    localStorage.setItem(localStorageKey, JSON.stringify(columnArr));
  }, [columnArr, localStorageKey]);

  return (
    <>
      {/* NOTE: for testing purposes */}
      {/* <Button variant="contained" onClick={triggerRefresh} style={{ marginBottom: theme.spacing(2) }}>
        Refresh
      </Button> */}
      <DndContext
        // sensors={sensors}
        // collisionDetection={closestCorners}
        collisionDetection={customCollisionDetectionAlgorithm}
        onDragStart={handleDragStart}
        onDragOver={handleDragOver}
        onDragEnd={handleDragEnd}
      >
        <div className={styles.board} style={{ gap: theme.spacing(2) }}>
          <SortableContext items={columnIds} strategy={horizontalListSortingStrategy}>
            {columnArr.map((column) => {
              const { id: status, expanded } = column;

              return (
                <KanbanColumn
                  key={`${status}-${refreshCounter}`}
                  column={column}
                  filters={filters}
                  canFetch={canFetch}
                  items={_.get(itemsDict, status, [])}
                  handleNewlyLoadedItems={handleNewlyLoadedItems(status)}
                  expanded={expanded}
                  handleExpand={() => handleExpandColumn(status)}
                  columnDataQuery={columnDataQuery}
                  columnDataQueryVarDict={columnDataQueryVarDict}
                  queryName={queryName}
                  total={_.get(columnTotals, status, 0)}
                  handleSetTotal={(total) => setColumnTotals((prev) => ({ ...prev, [status]: total }))}
                  renderCardContents={renderCardContents}
                />
              );
            })}
          </SortableContext>
        </div>
        {createPortal(
          <DragOverlay>
            {activeColumn && (
              <KanbanColumn
                column={_.omit(activeColumn, "items")}
                items={activeColumn.items}
                expanded={true}
                total={_.get(columnTotals, activeColumn?.id, 0)}
                columnDataQuery={columnDataQuery}
                renderCardContents={renderCardContents}
              />
            )}
            {activeCard && <KanbanCard item={activeCard} renderCardContents={renderCardContents} />}
          </DragOverlay>,
          document.body
        )}
      </DndContext>
    </>
  );
};

export default KanbanBoard;
